import * as React from 'react'

import { cn } from '../utils'
import { Icon } from './icon'

export type SearchProps = React.InputHTMLAttributes<HTMLInputElement> & {
  debounce?: number
  onSearch?: (value: string) => void
}

export const searchStyles =
  'flex gap-2 items-center h-10 text-md w-full rounded-md ' +
  'bg-input-background text-input-foreground border border-input-background ' +
  'hover:bg-input-background hover:border-input-background ' +
  'px-1 py-1 outline-0 outline-input-focus-shadow ' +
  'focus-within:ring focus-within:ring-input-focus-border focus-within:bg-input-background focus-within:outline-3 focus-within:outline-offset-1 '
const Search = React.forwardRef<HTMLInputElement, SearchProps>(
  ({ className, debounce = 200, onSearch, ...props }, ref) => {
    const timeout = React.useRef<number>(undefined)

    const handleChange = React.useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.currentTarget.value.trim()
        window.clearTimeout(timeout.current)
        timeout.current = window.setTimeout(() => {
          if (onSearch) {
            onSearch(value.trim())
          }
        }, debounce)
        if (props.onChange) props.onChange(e)
      },
      [debounce, onSearch, props],
    )

    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && onSearch) {
          onSearch(e.currentTarget.value)
        }
      },
      [onSearch],
    )

    return (
      <label
        className={cn(
          searchStyles,
          {
            'bg-input-secondary-background border-input-secondary-background flex':
              !!props.value,
          },
          className,
        )}
      >
        <Icon
          name="magnifying-glass"
          className="text-input-foreground h-5.5 -mt-px ml-1 shrink-0 p-0 opacity-50"
        />
        <input
          type="search"
          className="outline-hidden grow bg-transparent ring-0"
          ref={ref}
          {...props}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        />
      </label>
    )
  },
)
Search.displayName = 'Search'

export { Search }
